// RigSentinel Context Engine — Phase RCE-1
//
// MIGRATION WIZARD page (route: /migrate/projects)
// -------------------------------------------------
// Multi-step explicit migration of legacy operator/client/field/block
// strings on Wells & Rig Sheets into formal Project Profiles. NEVER
// auto-migrates — the user must confirm each group before the wizard
// applies it.
//
// Steps:
//   1) Scan & Group     — read-only, shows proposed groups
//   2) Review & Confirm — user can edit proposed names + uncheck groups
//   3) Apply            — runs applyMigrationPlan, shows results
//   4) Done             — back to home / settings

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Database, AlertCircle } from "lucide-react";
import {
  buildMigrationPlan, applyMigrationPlan,
  setMigrationPromptState,
} from "@/lib/rce/migration";

const STEPS = ["Scan", "Review & Confirm", "Apply", "Done"];

export default function MigrationWizard() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [plan, setPlan]   = useState(() => buildMigrationPlan());
  const [editable, setEditable] = useState([]);   // mutable copy of plan.groups
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);
  const [error, setError]   = useState("");

  useEffect(() => {
    setEditable(plan.groups.map((g) => ({ ...g })));
    setSelected(new Set(plan.groups.map((g) => _groupKey(g))));
  }, [plan]);

  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const apply = () => {
    try {
      const groupsToApply = editable.filter((g) => selected.has(_groupKey(g)));
      if (groupsToApply.length === 0) {
        setError("Select at least one group to migrate, or Dismiss the wizard.");
        return;
      }
      const r = applyMigrationPlan(groupsToApply);
      setResult(r);
      setStepIdx(3);
    } catch (e) {
      setError(e.message || "Migration failed.");
    }
  };

  const toggleSelect = (g) => {
    const k = _groupKey(g);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const onPatchGroup = (idx, patch) => {
    setEditable((prev) => prev.map((g, i) => i === idx ? { ...g, ...patch } : g));
  };

  return (
    <div className="pb-8" data-testid="migration-wizard">
      <div className="flex items-center gap-3 mb-4">
        <button
          data-testid="migration-back"
          onClick={() => navigate(-1)}
          className="btn-ghost h-9 w-9 rounded-md inline-flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading font-black text-xl">Project Profile Migration</h1>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--rs-text-3)]">
            RigSentinel Context Engine · Phase 1
          </div>
        </div>
      </div>

      <ol className="grid grid-cols-4 gap-1.5 mb-4">
        {STEPS.map((label, i) => (
          <li
            key={label}
            data-testid={`migration-step-${i}`}
            className="rounded px-2 py-1 text-center text-[10px] label-up font-semibold"
            style={i === stepIdx
              ? { background: "rgba(255,179,0,0.18)", color: "var(--rs-amber)", border: "1px solid rgba(255,179,0,0.5)" }
              : { color: "var(--rs-text-3)", background: "rgba(22,24,29,0.65)", border: "1px solid var(--rs-border)" }}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      {error && (
        <div
          data-testid="migration-error"
          className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 mb-4 text-rose-200 text-[12px]"
        >
          <AlertCircle className="w-3.5 h-3.5 inline-block mr-1" />
          {error}
        </div>
      )}

      {/* Step 1 — Scan */}
      {stepIdx === 0 && (
        <Step title="Scan complete" subtitle="The wizard found the following candidate Project Profiles. Review them on the next step.">
          {plan.groups.length === 0 ? (
            <EmptyState onDismiss={() => { setMigrationPromptState("dismissed"); navigate("/home"); }} />
          ) : (
            <ul className="space-y-2" data-testid="migration-scan-list">
              {plan.groups.map((g, i) => (
                <GroupRow key={i} g={g} testidBase={`scan-grp-${i}`} readOnly />
              ))}
            </ul>
          )}
        </Step>
      )}

      {/* Step 2 — Review & Confirm */}
      {stepIdx === 1 && (
        <Step title="Review & Confirm" subtitle="Edit the proposed name if needed, then uncheck any group you don't want to migrate.">
          {editable.length === 0 ? (
            <EmptyState onDismiss={() => { setMigrationPromptState("dismissed"); navigate("/home"); }} />
          ) : (
            <ul className="space-y-3" data-testid="migration-review-list">
              {editable.map((g, i) => (
                <li
                  key={i}
                  className="rounded-md border p-3 flex items-start gap-3"
                  style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
                >
                  <input
                    type="checkbox"
                    data-testid={`migration-grp-${i}-checkbox`}
                    checked={selected.has(_groupKey(g))}
                    onChange={() => toggleSelect(g)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <label className="block">
                      <div className="label-up text-[9.5px] text-[var(--rs-text-3)]">Project name</div>
                      <input
                        type="text"
                        data-testid={`migration-grp-${i}-name`}
                        value={g.proposedName}
                        onChange={(e) => onPatchGroup(i, { proposedName: e.target.value })}
                        className="w-full h-9 px-2 rounded border bg-[rgba(10,11,14,0.75)] text-[12px] text-[var(--rs-text-1)]"
                        style={{ borderColor: "var(--rs-border)" }}
                      />
                    </label>
                    <div className="text-[11px] text-[var(--rs-text-3)] flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><b>Operator:</b> {g.operator}</span>
                      {g.client && <span><b>Client:</b> {g.client}</span>}
                      {g.field && <span><b>Field:</b> {g.field}</span>}
                      {g.block && <span><b>Block:</b> {g.block}</span>}
                      {g.country && <span><b>Country:</b> {g.country}</span>}
                    </div>
                    <div className="text-[11px] text-[var(--rs-text-3)]">
                      Will bind <b>{g.wellIds.length}</b> well{g.wellIds.length === 1 ? "" : "s"}
                      {g.rigSheetIds.length > 0 ? `, ${g.rigSheetIds.length} rig sheet${g.rigSheetIds.length === 1 ? "" : "s"} (preview)` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Step>
      )}

      {/* Step 3 — Apply */}
      {stepIdx === 2 && (
        <Step title="Ready to apply" subtitle="The wizard will create the Project Profiles below and bind your existing wells to them. Reference Libraries and engineering calculations are unaffected.">
          <div className="rounded-md border p-3 text-[12px] text-[var(--rs-text-2)]" style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}>
            <Database className="w-3.5 h-3.5 inline-block mr-1" style={{ color: "var(--rs-amber)" }} />
            <b>{editable.filter((g) => selected.has(_groupKey(g))).length}</b> Project Profile(s) will be created.
          </div>
          <ul className="mt-3 space-y-2" data-testid="migration-apply-list">
            {editable.filter((g) => selected.has(_groupKey(g))).map((g, i) => (
              <GroupRow key={i} g={g} testidBase={`apply-grp-${i}`} readOnly />
            ))}
          </ul>
        </Step>
      )}

      {/* Step 4 — Done */}
      {stepIdx === 3 && result && (
        <Step title="Migration complete" subtitle="Your existing data has been organised into Project Profiles. Future modules will inherit operator and client identity automatically.">
          <ul className="space-y-2" data-testid="migration-results">
            {result.created.map((r, i) => (
              <li
                key={i}
                data-testid={`migration-result-${i}`}
                className="rounded-md border p-3 flex items-start gap-3"
                style={{ borderColor: "rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.06)" }}
              >
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                <div className="flex-1 text-[12px]">
                  <div className="font-semibold text-emerald-300">{r.projectName}</div>
                  <div className="text-[var(--rs-text-3)] text-[11px]">
                    {r.wellsBound.length} well(s) bound · {r.rigSheetIds.length} rig sheet(s) tagged
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-2">
            <Link to="/home" className="h-9 px-3 rounded text-[12px] label-up font-semibold inline-flex items-center" style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }} data-testid="migration-done">
              Back to home
            </Link>
          </div>
        </Step>
      )}

      {/* Footer nav */}
      {stepIdx < 3 && (
        <div className="flex gap-2 mt-4">
          {stepIdx > 0 && (
            <button
              data-testid="migration-prev"
              onClick={back}
              className="btn-ghost h-9 px-3 rounded text-[12px] label-up inline-flex items-center gap-1.5"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}
          <div className="flex-1" />
          {stepIdx === 0 && plan.groups.length > 0 && (
            <button
              data-testid="migration-next-scan"
              onClick={next}
              className="h-9 px-3 rounded text-[12px] label-up font-semibold inline-flex items-center gap-1.5"
              style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
            >
              Continue <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
          {stepIdx === 1 && (
            <button
              data-testid="migration-next-review"
              onClick={next}
              className="h-9 px-3 rounded text-[12px] label-up font-semibold inline-flex items-center gap-1.5"
              style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
            >
              Continue <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
          {stepIdx === 2 && (
            <button
              data-testid="migration-apply"
              onClick={apply}
              className="h-9 px-3 rounded text-[12px] label-up font-semibold"
              style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
            >
              Apply Migration
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <section>
      <h2 className="font-heading font-black text-lg">{title}</h2>
      <p className="text-[12px] text-[var(--rs-text-3)] mb-3">{subtitle}</p>
      {children}
    </section>
  );
}

function GroupRow({ g, testidBase, readOnly }) {
  return (
    <li
      data-testid={testidBase}
      className="rounded-md border p-3"
      style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
    >
      <div className="text-[13px] font-semibold text-[var(--rs-text-1)]">{g.proposedName}</div>
      <div className="text-[11px] text-[var(--rs-text-3)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        <span><b>Operator:</b> {g.operator || "—"}</span>
        {g.client && <span><b>Client:</b> {g.client}</span>}
        {g.field && <span><b>Field:</b> {g.field}</span>}
        {g.block && <span><b>Block:</b> {g.block}</span>}
        {g.country && <span><b>Country:</b> {g.country}</span>}
        <span><b>Wells:</b> {g.wellIds.length}</span>
        {g.rigSheetIds.length > 0 && <span><b>Rig sheets:</b> {g.rigSheetIds.length}</span>}
      </div>
    </li>
  );
}

function EmptyState({ onDismiss }) {
  return (
    <div
      data-testid="migration-empty"
      className="rounded-md border p-4 text-[12px] text-[var(--rs-text-2)]"
      style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
    >
      No legacy operator/client strings were detected. Nothing to migrate.
      <div className="mt-3">
        <button
          data-testid="migration-empty-dismiss"
          onClick={onDismiss}
          className="btn-ghost h-8 px-3 rounded text-[11px] label-up"
        >
          OK
        </button>
      </div>
    </div>
  );
}

function _groupKey(g) {
  return [g.operator, g.client, g.field, g.block]
    .map((x) => String(x || "").trim().toLowerCase()).join("|");
}
